// Global Activity Log API — reads farm-wide activity through the
// list_global_activity SECDEF RPC. Does not query activity_events
// or activity_mentions directly.

import type {SupabaseClient} from './supabaseTypes.js';

// Every filter is optional and is sent ONLY when set — an explicit null would
// mean something different to the RPC than omitting the argument — so the
// params object is built up rather than declared whole.
export type GlobalActivityFilters = {
  limit?: number;
  before?: string;
  entityType?: string;
  eventType?: string;
  search?: string;
  /** Query cancellation supplied by the paginated route-resource runtime. */
  signal?: AbortSignal;
};

type GlobalActivityParams = {
  p_limit: number;
  p_before?: string;
  p_entity_type?: string;
  p_event_type?: string;
  p_search?: string;
};

// list_global_activity RETURNS TABLE, so this resolves to rows. Their column
// shape lives in SQL and this repository has no generated database types, so
// rows stay `unknown` rather than being asserted into an invented interface.
export async function loadGlobalActivity(
  sb: SupabaseClient,
  {limit = 50, before, entityType, eventType, search, signal}: GlobalActivityFilters = {},
): Promise<unknown[]> {
  if (!sb) return [];
  const params: GlobalActivityParams = {p_limit: limit};
  if (before) params.p_before = before;
  if (entityType) params.p_entity_type = entityType;
  if (eventType) params.p_event_type = eventType;
  if (search) params.p_search = search;
  const request = sb.rpc('list_global_activity', params);
  const response = signal && typeof request.abortSignal === 'function' ? request.abortSignal(signal) : request;
  const {data, error} = await response;
  if (error) throw new Error(`loadGlobalActivity: ${error.message}`);
  return data || [];
}
